/**
 * RouteListItem Component
 *
 * A reusable list row for displaying route information.
 * Used by RouteRow (climb list) and ClimbRow (history view).
 *
 * Features:
 * - RouteCircle with hold color fill, optional tag color ring, and grade text
 * - Route name (primary) + wall section (secondary), or wall section only if no name
 * - Customizable right-side content via children
 *
 * @example
 * ```tsx
 * <RouteListItem
 *   name=""
 *   wallSection="Storage Boulder"
 *   officialGrade="V3"
 *   identification={{ holdColor: 'Yellow', tagColor: 'Blue' }}
 *   colorGrades={gym.colorGrades}
 *   onClick={() => handleClick()}
 * >
 *   <span>Feb 4</span>
 *   <span>0 sends</span>
 * </RouteListItem>
 * ```
 */

import { type CSSProperties, type ReactNode, memo } from 'react';
import type { VGrade, GradeModifier, RouteIdentification } from '@flash/shared';
import { RouteCircle } from '@/components/icons/RouteCircle';

/**
 * Minimal color grade info needed for color lookup
 */
interface ColorGradeInfo {
  displayName: string;
  color: string;
}

/**
 * Props for RouteListItem component
 */
export interface RouteListItemProps {
  /** Route name (may be empty) */
  name: string;
  /** Wall section where route is located */
  wallSection: string;
  /** Official grade */
  officialGrade: VGrade | null;
  /** Grade modifier */
  officialGradeModifier?: GradeModifier | null;
  /** Route identification colors (color names, not hex) */
  identification?: RouteIdentification;
  /** Legacy color grade name (fallback if no identification) */
  colorGrade?: string;
  /** Gym's color grades for hold colors */
  colorGrades: ColorGradeInfo[];
  /** Gym's tag color grades (only used when colors are unlinked) */
  tagColorGrades?: ColorGradeInfo[];
  /** Called when the row is clicked */
  onClick?: () => void;
  /** Aria label for accessibility */
  ariaLabel?: string;
  /** Whether to dim the row (e.g., for sent routes) */
  dimmed?: boolean;
  /** Right-side content */
  children?: ReactNode;
}

/**
 * Format a grade with optional modifier for display
 */
const formatGrade = (grade: VGrade | null, modifier?: GradeModifier | null): string => {
  if (!grade) return '';
  if (!modifier) return grade;
  return `${grade}${modifier}`;
};

/**
 * Get the hex color value for a color name from color grades
 */
const getColorHex = (
  colorName: string | undefined,
  colorGrades: ColorGradeInfo[]
): string | null => {
  if (!colorName) return null;
  const colorGrade = colorGrades.find((cg) => cg.displayName === colorName);
  return colorGrade?.color ?? null;
};

/**
 * RouteListItem Component
 */
export const RouteListItem = memo(function RouteListItem({
  name,
  wallSection,
  officialGrade,
  officialGradeModifier,
  identification,
  colorGrade,
  colorGrades,
  tagColorGrades,
  onClick,
  ariaLabel,
  dimmed = false,
  children,
}: RouteListItemProps) {
  // Determine which color grades to use for tag lookups
  const effectiveTagColorGrades = tagColorGrades ?? colorGrades;

  // Get hold and tag colors from route identification
  const holdColorHex = getColorHex(identification?.holdColor, colorGrades);
  const tagColorHex = getColorHex(identification?.tagColor, effectiveTagColorGrades);

  // Fallback to legacy colorGrade if no identification colors
  const hasIdentificationColors = holdColorHex || tagColorHex;
  const fallbackColor = !hasIdentificationColors
    ? (colorGrades.find((cg) => cg.displayName === colorGrade)?.color ?? '#808080')
    : null;

  const gradeText = formatGrade(officialGrade, officialGradeModifier);
  const displayName = name || wallSection;
  const hasSecondaryText = name && wallSection;

  // Generate default aria label if not provided
  const defaultAriaLabel = `${gradeText || wallSection}${name ? ` ${name}` : ''}`;

  const content = (
    <>
      {/* Route Circle */}
      <div style={circleContainerStyle} aria-hidden="true">
        <RouteCircle
          grade={gradeText}
          holdColor={holdColorHex ?? fallbackColor ?? '#808080'}
          tagColor={tagColorHex ?? undefined}
          size={24}
        />
      </div>

      {/* Text Content */}
      <div style={textContentStyle}>
        <span style={primaryTextStyle}>{displayName}</span>
        {hasSecondaryText && <span style={secondaryTextStyle}>{wallSection}</span>}
      </div>

      {/* Right-side content */}
      {children && <div style={rightContentStyle}>{children}</div>}
    </>
  );

  // Render as button only if onClick is provided
  if (onClick) {
    return (
      <button
        type="button"
        style={{
          ...rowStyle,
          ...(dimmed ? dimmedRowStyle : {}),
        }}
        onClick={onClick}
        aria-label={ariaLabel ?? defaultAriaLabel}
      >
        {content}
      </button>
    );
  }

  // Render as div when no onClick (e.g., in selection mode)
  return (
    <div
      style={{
        ...rowStyle,
        ...(dimmed ? dimmedRowStyle : {}),
      }}
      aria-label={ariaLabel ?? defaultAriaLabel}
    >
      {content}
    </div>
  );
});

// Styles - matches RouteRow card styling
const rowStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: '12px',
  width: 'calc(100% - 16px)',
  margin: '8px',
  padding: '14px 16px',
  backgroundColor: 'var(--color-bg-primary, white)',
  border: 'none',
  borderRadius: '12px',
  boxShadow: '0 1px 3px rgba(0, 0, 0, 0.08), 0 1px 2px rgba(0, 0, 0, 0.06)',
  cursor: 'pointer',
  textAlign: 'left',
  transition: 'background-color 150ms ease-out, box-shadow 150ms ease-out',
};

const dimmedRowStyle: CSSProperties = {
  opacity: 0.6,
};

const circleContainerStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  flexShrink: 0,
};

const textContentStyle: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: '2px',
  minWidth: 0,
  flex: 1,
};

const primaryTextStyle: CSSProperties = {
  fontSize: '0.875rem',
  fontWeight: 600,
  color: 'var(--color-text-primary, #1f2937)',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
};

const secondaryTextStyle: CSSProperties = {
  fontSize: '0.75rem',
  color: 'var(--color-text-secondary, #6b7280)',
};

const rightContentStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: '12px',
  flexShrink: 0,
};

export default RouteListItem;
